import { Box, CircularProgress, Typography } from '@mui/material'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Attachment } from '../../../utils/fixtures'
import { resolveNestedObject } from '../../../utils/helper'
import {
  AddressAutocomplete,
  Button,
  CheckBox,
  DatePicker,
  DatePickerProps,
  Input,
  Item,
  PhoneFieldWithCall,
  QuerySelect,
  RadioButtonGroup,
  Rating,
  Select,
  Switch
} from '../../atoms'
import BillingButton from '../BillingButton'
import CollisionDetection from '../CollisionDetection'
import CompanyAutoComplete from '../CompanyAutoComplete'
import DisputeValueModal from '../DisputeValueModal'
import DraftInvoiceButton from '../DraftInvoiceButton'
import ExportButton from '../ExportButton'
import ExternalRsv from '../ExternalRsv'
import FilesList from '../FilesList'
import ForwardRightmartButton from '../ForwardRightmartButton'
import CreateAkteButton from '../CreateAkteButton'
import JurnodesButton from '../JurnodesButton'
import RequestCoverageButton from '../RequestCoverageButton'
import SMSButton from '../SMSButton'
import TextfieldWithAi from '../TextfieldWithAi'
import Uploader from '../Uploader'
import RichTextEditor from './Components/RichTextComponent'

interface IRadioItems {
  label: string
  value: any
}

interface IValueLabel {
  value: any
  label: string
}

interface IQueryOptions {
  showOnlyField?: IValueLabel
  renderUnknownField?: boolean
  autoSelectSingleOption?: boolean
  variables: any
  labelField: string
  valueField: string
}

type WatchMethod = (name: string) => any

interface IOptions {
  dependency?: string
  split?: boolean
  format?: (value: any) => string
  minWidth?: string | number
  sx?: any
  onSelected?: (value: any) => void
  onChange?(value: any): void
  onBlur?(value: any): void
  items?: Item[]
  autosize?: boolean
  rows?: number
  url?: string
  children?: React.ReactNode
  parentSx?: any
  additionalSelectFields?: Item[]
  onClick?: () => void
  icon?: React.JSX.Element
  loading?: boolean
  withNullField?: boolean
  subject?: string
  body?: string
  caseEmailContext?: { caseId: string; processingId?: string }
  onBlurValidOnly?: boolean
  helperTextColor?: string | undefined
  onSavePreset?: () => void
  type?: string
  withPlaceholder?: boolean
  mask?: string
  maskChar?: string
  alwaysShowMask?: boolean
  permanents?: number[]
  context?: string
  name?: string
  watch?: WatchMethod
  setValue?: (name: string, value: any) => void
  prefix?: string
  processingId?: string
  externalUrl?: string
}

export interface IProps {
  name: string
  callback?: () => void
  query?: any
  mutation?: any
  queryOptions?: IQueryOptions
  label?: string
  disabled?: boolean
  shouldUnregister?: boolean
  helperText?: string
  dialogContent?: any
  onChangeProcessingId?: (value: string | string[]) => void
  options?: IOptions
  radioItems?: IRadioItems[]
  type?:
    | 'time'
    | 'loading'
    | 'date'
    | 'datetime'
    | 'typography'
    | 'value'
    | 'export'
    | 'text'
    | 'smsButton'
    | 'collisionDetection'
    | 'requestCoverageButton'
    | 'forwardRightmartButton'
    | 'billingButton'
    | 'dropdown'
    | 'select'
    | 'rating'
    | 'boolean'
    | 'richtext'
    | 'textfield'
    | 'textfieldWithAi'
    | 'checkbox'
    | 'phone'
    | 'phoneWithCall'
    | 'radio'
    | 'switch'
    | 'uploader'
    | 'money'
    | 'disputeValue'
    | 'files'
    | 'default'
    | 'actionButton'
    | 'email'
    | 'companySearch'
    | 'addressSearch'
    | 'copyAddress'
    | 'copyable'
    | 'externalLink'
    | 'draftInvoiceButton'
    | 'divider'
    | 'sectionHeader'
    | 'jurnodesSyncButton'
    | 'createAkteButton'
    | 'externalRsv'
}

const FormItem: React.FunctionComponent<IProps> = ({
  query = undefined,
  queryOptions = undefined,
  mutation = undefined,
  label = '',
  disabled = false,
  name,
  helperText = undefined,
  dialogContent = undefined,
  options = {},
  radioItems = undefined,
  type = 'default',
  callback = undefined,
  onChangeProcessingId = undefined,
  shouldUnregister = false
}) => {
  const { t } = useTranslation(['common', 'validation'])
  const {
    control,
    getFieldState,
    getValues,
    formState: { errors }
  } = useFormContext()

  const handleFieldBlurFromEvent = async (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    onBlur: any
  ) => {
    onBlur?.(e)
    if (options?.onBlurValidOnly) {
      const fieldState = getFieldState(name)

      if (
        (!fieldState?.invalid && fieldState?.isDirty) ||
        (fieldState?.isTouched && getValues(name))
      ) {
        options?.onBlur?.(e)
      }
    } else {
      options?.onBlur?.(e)
    }
  }

  // @ts-ignore
  const handleFieldChangeFromEvent = ({ target: { value } }, onChange) => {
    onChange(value)
    options?.onChange?.(value)
  }
  // @ts-ignore
  const handleFieldChangeFromValue = (value, onChange) => {
    onChange(value)
    options?.onChange?.(value)
  }
  // @ts-ignore
  const handleSwitchChange = ({ target: { checked } }, onChange) => {
    onChange(checked)
    options?.onChange?.(checked)
  }

  const getFilesFromValue = (value?: Attachment[]) =>
    value?.map((file: Attachment) => ({
      ...file,
      filename: file.attachmentFilename,
      url: file.attachmentUrl
    }))

  const filesComponent = (value?: Attachment[]) => {
    const files = getFilesFromValue(value)
    // @ts-ignore
    return <FilesList {...options} files={files} label={label} />
  }

  // ref intentionally omitted: DatePicker is not a forwardRef component and does not
  // accept ref in its props. React 19 no longer allows implicit ref on function components.
  const dateComponent = (datePickerProps: DatePickerProps) => (
    <DatePicker
      {...options}
      onChange={e => handleFieldChangeFromValue(e, datePickerProps.onChange)}
      value={datePickerProps.value}
      label={label}
      type={datePickerProps.type}
      fullWidth
      disabled={disabled}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      error={resolveNestedObject(name, errors)}
    />
  )
  // @ts-ignore
  const dropdownComponent = (onChange, value, ref: React.Ref<any>) => (
    <QuerySelect
      id={name}
      ref={ref}
      // @ts-ignore
      onChange={e => handleFieldChangeFromValue(e, onChange)}
      // @ts-ignore
      value={value}
      error={resolveNestedObject(name, errors)}
      label={label}
      // @ts-ignore
      name={queryOptions?.variables?.filter?.lookupType || undefined}
      query={query}
      options={queryOptions}
      additionalSelectFields={options?.additionalSelectFields}
      withNullField={options?.withNullField}
      disabled={disabled}
      minWidth="100%"
      maxWidth={options?.maxWidth}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      showOnlyField={queryOptions?.showOnlyField}
      renderUnknownField={queryOptions?.renderUnknownField}
      sx={options?.sx}
    />
  )
  // @ts-ignore
  const selectComponent = (onChange, value, ref: React.Ref<any>) => (
    <Select
      id={name}
      ref={ref}
      // @ts-ignore
      onChange={e => handleFieldChangeFromValue(e, onChange)}
      // @ts-ignore
      value={value}
      error={resolveNestedObject(name, errors)}
      label={label}
      // @ts-ignore
      name={queryOptions?.variables?.filter?.lookupType || undefined}
      disabled={disabled}
      minWidth={options?.minWidth || '100%'}
      items={options?.items || []}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      showOnlyField={queryOptions?.showOnlyField}
      sx={options?.parentSx}
    />
  )
  // const dropdownComponent = (
  //   querySelectProps: QuerySelectProps,
  //   ref: ForwardedRef<JSX.Element>
  // ) => (
  //   <QuerySelect
  //     ref={ref}
  //     // @ts-ignore
  //     onChange={e => handleFieldChangeFromValue(e, querySelectProps.onChange)}
  //     value={querySelectProps.value}
  //     label={label}
  //     // @ts-ignore
  //     name={queryOptions?.variables?.filter?.lookupType || undefined}
  //     query={query}
  //     options={queryOptions}
  //     disabled={disabled}
  //     // helperText={helperText}
  //     // showOnlyField={queryOptions?.showOnlyField}
  //   />
  // )
  // @ts-ignore
  const ratingComponent = (onChange, value, ref) => (
    <Rating
      id={name}
      ref={ref}
      // @ts-ignore
      value={value}
      // @ts-ignore
      onChange={val => handleFieldChangeFromValue(val, onChange)}
      disabled={disabled}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      error={resolveNestedObject(name, errors)}
    />
  )

  const booleanComponent = (onChange: any, value: any, ref: any) => (
    <Select
      id={name}
      ref={ref}
      value={value}
      onChange={val => handleFieldChangeFromValue(val, onChange)}
      minWidth="100%"
      label={label}
      items={[
        {
          label: t('common:yes'),
          value: true
        },
        {
          label: t('common:no'),
          value: false
        }
      ]}
      disabled={disabled}
      helperText={helperText}
    />
  )
  // @ts-ignore
  const textfieldComponent = (onBlur, onChange, value, ref) => (
    <Input
      {...options}
      id={name}
      noPlaceholder
      ref={ref}
      value={value}
      onChange={e => handleFieldChangeFromEvent(e, onChange)}
      onBlur={e => handleFieldBlurFromEvent(e, onBlur)}
      multiline
      // @ts-ignore
      rows={options?.autosize ? undefined : options?.rows || 5}
      fullWidth
      // @ts-ignore
      type={type}
      label={label}
      disabled={disabled}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      error={resolveNestedObject(name, errors)}
    />
  )

  const textfieldWithAiComponent = (onBlur: any, onChange: any, value: any, ref: any) => (
    <TextfieldWithAi
      name={name}
      ref={ref}
      value={value}
      onChange={(e: any) => handleFieldChangeFromEvent(e, onChange)}
      onBlur={(e: any) => handleFieldBlurFromEvent(e, onBlur)}
      // @ts-ignore
      type={type}
      label={label}
      disabled={disabled}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      error={resolveNestedObject(name, errors)}
      options={options}
    />
  )
  const renderDisputeValue = (onBlur: any, onChange: any, value: any, ref: any) => (
    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
      <Input
        {...options}
        id={name}
        noPlaceholder={!options?.withPlaceholder}
        ref={ref}
        value={value}
        onChange={e => handleFieldChangeFromEvent(e, onChange)}
        onBlur={e => handleFieldBlurFromEvent(e, onBlur)}
        fullWidth
        type="money"
        label={label}
        disabled={disabled}
        helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
        error={resolveNestedObject(name, errors)}
      />
      <DisputeValueModal
        watch={options?.watch as WatchMethod}
        name={options?.name as string}
        setValue={options?.setValue}
      />
    </Box>
  )
  // @ts-ignore
  const inputComponent = (onBlur, onChange, value, ref) => (
    <Input
      {...options}
      id={name}
      name={name}
      noPlaceholder={!options?.withPlaceholder}
      ref={ref}
      value={value}
      onChange={e => handleFieldChangeFromEvent(e, onChange)}
      onBlur={e => handleFieldBlurFromEvent(e, onBlur)}
      fullWidth
      // @ts-ignore
      type={type}
      label={label}
      disabled={disabled}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      error={resolveNestedObject(name, errors)}
    />
  )

  const loadingComponent = (ref: React.Ref<any>) => (
    <div style={options?.parentSx || {}}>
      <Typography {...options}>{label}</Typography>
      <Box
        sx={{
          padding: 2,
          height: '100px'
        }}
        ref={ref}
      >
        <CircularProgress sx={{ color: 'inherit' }} />
      </Box>
    </div>
  )

  const copyAddressComponent = (
    onBlur: () => void,
    onChange: (event: any) => void,
    value: string,
    ref: any
  ) => (
    <Input
      {...options}
      id={name}
      noPlaceholder
      ref={ref}
      value={value}
      onChange={e => handleFieldChangeFromEvent(e, onChange)}
      onBlur={e => handleFieldBlurFromEvent(e, onBlur)}
      fullWidth
      // @ts-ignore
      type={type}
      label={label}
      disabled={disabled}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      error={resolveNestedObject(name, errors)}
    />
  )
  // @ts-ignore
  const checkBoxComponent = (onChange, value, ref) => (
    <CheckBox
      id={name}
      ref={ref}
      // @ts-ignore
      checked={value}
      value={value}
      // @ts-ignore
      onChange={val => handleFieldChangeFromValue(val, onChange)}
      label={label}
      disabled={disabled}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      error={resolveNestedObject(name, errors)}
    />
  )

  const textComponent = () => (
    <div style={{ display: 'flex', width: '100%' }}>
      <Typography variant="h1">{label}</Typography>
    </div>
  )
  const valueComponent = (value: string) => (
    <div style={options?.parentSx || {}}>
      <Typography {...options}>
        {
          // @ts-ignore
          options?.format ? options.format(value) : value
        }
      </Typography>
    </div>
  )
  const typographyComponent = () => (
    // @ts-ignore
    <div style={options?.parentSx || {}}>
      <Typography {...options}>{label}</Typography>
    </div>
  )

  // @ts-ignore
  const collisionDetectionComponent = () => <CollisionDetection disabled={disabled} />

  // @ts-ignore
  const smsButtonComponent = (onChange, value) => (
    <SMSButton
      mutation={mutation}
      options={queryOptions}
      label={label || ''}
      value={value}
      disabled={disabled}
      helperText={helperText}
      dialogContent={dialogContent}
    />
  )

  const billingButtonComponent = (value: any) => (
    <BillingButton
      mutation={mutation}
      queryOptions={queryOptions}
      options={options}
      value={value}
      label={label}
      disabled={disabled}
      callback={callback}
      helperText={helperText}
      onChangeProcessingId={onChangeProcessingId}
    />
  )
  // @ts-ignore
  const phoneInputComponent = (onBlur, onChange, value, ref) => (
    <Input
      {...options}
      id={name}
      noPlaceholder
      name={name}
      ref={ref}
      value={value}
      onChange={e => handleFieldChangeFromEvent(e, onChange)}
      onBlur={e => handleFieldBlurFromEvent(e, onBlur)}
      fullWidth
      // @ts-ignore
      type={type}
      label={label}
      disabled={disabled}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      error={resolveNestedObject(name, errors)}
    />
  )

  // @ts-ignore
  const phoneWithCallComponent = (onBlur: any, onChange: any, value: any, ref: any) => (
    <PhoneFieldWithCall
      {...options}
      id={name}
      name={name}
      ref={ref}
      value={value}
      onChange={(e: any) => handleFieldChangeFromEvent(e, onChange)}
      onBlur={(e: any) => handleFieldBlurFromEvent(e, onBlur)}
      fullWidth
      label={label}
      disabled={disabled}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      error={resolveNestedObject(name, errors)}
    />
  )
  // @ts-ignore
  const radioGroupComponent = (onChange, value, ref) => (
    <RadioButtonGroup
      {...options}
      ref={ref}
      id={name}
      // @ts-ignore
      value={value}
      // @ts-ignore
      onChange={e => handleFieldChangeFromValue(e, onChange)}
      label={label}
      disabled={disabled}
      items={radioItems}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      error={resolveNestedObject(name, errors)}
      name={name}
    />
  )
  // @ts-ignore
  const uploadComponent = (onChange, value, ref) => {
    const files = getFilesFromValue(value)
    // @ts-ignore
    return <Uploader {...options} ref={ref} small value={files} disabled={disabled} name={name} />
  }
  // @ts-ignore
  const switchComponent = (onChange, value, ref) => (
    <Box sx={options?.parentSx}>
      <Switch
        id={name}
        {...options}
        ref={ref}
        // @ts-ignore
        onChange={e => handleSwitchChange(e, onChange)}
        label={label}
        small
        value={value}
        disabled={disabled}
        checked={value}
        defaultChecked={value}
        name={name}
        error={resolveNestedObject(name, errors)}
        helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
      />
      {value && options?.children}
    </Box>
  )
  // @ts-ignore
  const renderExternalRsv = (onChange, value, ref) => (
    <ExternalRsv
      label={label}
      onChange={e => handleFieldChangeFromValue(e, onChange)}
      ref={ref}
      value={value}
      disabled={disabled}
      error={resolveNestedObject(name, errors)}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
    />
  )

  // @ts-ignore
  const renderCompanyAutoComplete = (onChange, value, ref) => (
    <CompanyAutoComplete
      label={label}
      query={query}
      queryOptions={queryOptions}
      onChange={e => handleFieldChangeFromValue(e, onChange)}
      ref={ref}
      onSelected={options?.onSelected}
      value={value}
      onSavePreset={options?.onSavePreset}
    />
  )

  // @ts-ignore
  const renderAddressAutoComplete = (onChange, value, ref) => (
    <AddressAutocomplete
      label={label}
      onChange={e => handleFieldChangeFromValue(e, onChange)}
      onSelected={options?.onSelected}
      ref={ref}
      value={value}
      name={name}
      options={options}
      disabled={disabled}
      error={resolveNestedObject(name, errors)}
      helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
    />
  )
  if (type === 'jurnodesSyncButton') {
    return <JurnodesButton disabled={disabled} />
  }

  if (type === 'createAkteButton') {
    return <CreateAkteButton disabled={disabled} />
  }

  if (type === 'divider') {
    return <Box sx={{ width: '100%', borderBottom: '1px solid', borderColor: 'divider', my: 2 }} />
  }

  if (type === 'sectionHeader') {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {label}
      </Typography>
    )
  }

  if (type === 'actionButton') {
    return (
      <>
        <Button
          key={name || label || 'actionButton'}
          loading={options?.loading}
          startIcon={options?.icon}
          color="primary"
          onClick={options?.onClick}
          fullWidth
          disabled={disabled}
        >
          {label}
        </Button>
        {helperText && (
          <Typography
            variant="caption"
            sx={{ paddingTop: 1, display: 'flex', margin: '0px 14px', width: '100%' }}
          >
            {helperText}
          </Typography>
        )}
      </>
    )
  }
  if (type === 'draftInvoiceButton')
    return <DraftInvoiceButton options={options} disabled={disabled} helperText={helperText} />

  if (type === 'export')
    return (
      <ExportButton label={label} disabled={disabled} helperText={helperText} options={options} />
    )

  if (type === 'requestCoverageButton')
    return (
      <RequestCoverageButton
        options={queryOptions}
        label={label || ''}
        disabled={disabled}
        helperText={helperText}
        dialogContent={dialogContent}
      />
    )
  if (type === 'forwardRightmartButton')
    return (
      <ForwardRightmartButton
        options={queryOptions}
        label={label || ''}
        disabled={disabled}
        helperText={helperText}
        dialogContent={dialogContent}
      />
    )

  return (
    <Controller
      control={control}
      name={name}
      shouldUnregister={shouldUnregister}
      render={({ field: { onBlur, onChange, value, ref } }) => (
        <>
          {type === 'time' && dateComponent({ onChange, type, value })}
          {type === 'date' && dateComponent({ onChange, type, value })}
          {type === 'datetime' && dateComponent({ onChange, type, value })}
          {type === 'dropdown' && dropdownComponent(onChange, value, ref)}
          {type === 'select' && selectComponent(onChange, value, ref)}
          {/* {type === 'dropdown' && dropdownComponent({ onChange, value }, ref)} */}
          {type === 'typography' && typographyComponent()}
          {type === 'value' && valueComponent(value)}
          {
            // @ts-ignore
            type === 'text' && textComponent(onChange, value)
          }
          {type === 'billingButton' && billingButtonComponent(value)}
          {type === 'collisionDetection' && collisionDetectionComponent()}
          {type === 'smsButton' && smsButtonComponent(onChange, value)}
          {type === 'rating' && ratingComponent(onChange, value, ref)}
          {type === 'boolean' && booleanComponent(onChange, value, ref)}
          {type === 'textfield' && textfieldComponent(onBlur, onChange, value, ref)}
          {type === 'richtext' && (
            <RichTextEditor
              label={label}
              disabled={disabled}
              onChange={onChange}
              value={value}
              onBlur={onBlur}
              placeholder={helperText}
              error={!!resolveNestedObject(name, errors)}
              helperText={t(resolveNestedObject(name, errors)?.message) || helperText}
            />
          )}
          {type === 'textfieldWithAi' && textfieldWithAiComponent(onBlur, onChange, value, ref)}
          {type === 'checkbox' && checkBoxComponent(onChange, value, ref)}
          {type === 'phone' && phoneInputComponent(onBlur, onChange, value, ref)}
          {type === 'phoneWithCall' && phoneWithCallComponent(onBlur, onChange, value, ref)}
          {type === 'radio' && radioGroupComponent(onChange, value, ref)}
          {type === 'switch' && switchComponent(onChange, value, ref)}
          {type === 'uploader' && uploadComponent(onChange, value, ref)}
          {type === 'files' && filesComponent(value)}
          {type === 'companySearch' && renderCompanyAutoComplete(onChange, value, ref)}
          {type === 'addressSearch' && renderAddressAutoComplete(onChange, value, ref)}
          {type === 'externalRsv' && renderExternalRsv(onChange, value, ref)}
          {type === 'disputeValue' && renderDisputeValue(onBlur, onChange, value, ref)}
          {(type === 'money' || type === 'email' || type === 'default') &&
            inputComponent(onBlur, onChange, value, ref)}
          {type === 'copyAddress' && copyAddressComponent(onBlur, onChange, value, ref)}
          {type === 'copyable' && copyAddressComponent(onBlur, onChange, value, ref)}
          {type === 'externalLink' && copyAddressComponent(onBlur, onChange, value, ref)}
          {type === 'loading' && loadingComponent(ref)}
        </>
      )}
    />
  )
}

export default React.memo(FormItem)
